import { docsService, vinSessionService } from '@web/services/singletons';
import { useVinSessionDispatch } from '../context/vin-sessions.hooks';
import { VinSessionActionDispatcher, vinSessionActions } from '../context/vin-sessions.state';
import { AccountDocsType, PageDetail, VIN } from '../types';
import { getLogger } from '@packages/core/logging';
import { Account } from '@packages/models/api';
import { PublicationsActionDispatcher } from '../components/stis-publications/context/publications.state';

const logger = getLogger('vin-sessions');

export const filterList = [
    'QMR Report',
    'techline case',
    'Accessory Installation Guide',
    'Owner manual',
    'On-Board Diagnostics',
    'Maintainance Advisory Bulletin',
    'TechTIPS NewsLetter',
    'Campaign Bulletin',
    'Service Manual',
    'Service Manual Correction',
    'Video',
    'Warranty Bulletin',
    'Technical Reference Booklet',
    'Technical Service Bulletin',
    'Troubleshooting Guide',
    'Warranty Booklet',
    'Service Diagnostics',
];

export const publicationTypes = [
    // { icon: 'car', publicationType: 'QMR Reports' },
    // { icon: 'phone', publicationType: 'Techline Cases' },
    // { icon: 'battery', publicationType: 'BMIS Test' },
    { icon: 'treeTroubleshooting', publicationType: 'Troubleshooting Guide' },
    { icon: 'techTips', publicationType: 'TechTIPS NewsLetter' },
    { icon: 'menuBook', publicationType: 'Service Manual' },
    { icon: 'guides', publicationType: 'Accessory Installation Guide' },
    { icon: 'speedVelocimeter', publicationType: 'On-Board Diag II Information' },
    { icon: 'accountManager', publicationType: 'Owner Manual' },
    { icon: 'menuBookCorrection', publicationType: 'Service Manual Correction' },
    { icon: 'oilCan', publicationType: 'State Inspection/Maintenance Advisory Bulletin' },
    { icon: 'pushPin', publicationType: 'Subaru Product/Campaign Bulletin' },
    { icon: 'pushPin', publicationType: 'Technical Service Bulletin' },
    { icon: 'speedVelocimeter', publicationType: 'Service Diagnostics' },
    { icon: 'booklet', publicationType: 'Technician Reference Booklet' },
    { icon: 'video', publicationType: 'Video Training Reference/Video' },
    { icon: 'carCheck', publicationType: 'Warranty Booklet' },
    { icon: 'carCheck', publicationType: 'Warranty Bulletin' },
];

export const updateRectAttrs = (rectElement: SVGRectElement) => {
    rectElement.setAttribute('x', '-15');
    rectElement.setAttribute('y', '0');
    rectElement.setAttribute('width', '85');
    rectElement.setAttribute('height', '40');
};

export const dateRange = ['Today', 'Last 7 days', 'Last 30 days', 'This year', 'Last year', 'Custom date range'];

export function handleVinSessionError(message: string, error?: Error) {
    logger.error(message, { error });
}

export function handleDocsError(message: string, error?: Error) {
    logger.error(message, { error });
}

export function handleSelectedFilters(filter: string, selectedFilters: any, vinSessionActions: any, dispatch: any) {
    if (selectedFilters?.includes(filter)) {
        const updatedFilters = selectedFilters.filter((f: string) => f !== filter);
        dispatch(
            vinSessionActions.setSelectedFilters({
                selectedFilters: updatedFilters,
            })
        );
        return;
    }
    dispatch(
        vinSessionActions.setSelectedFilters({
            selectedFilters: [...selectedFilters, filter],
        })
    );
}

export async function openVinSession(
    vin: string,
    accountId: any,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    selectedVin: VIN | undefined
) {
    if (selectedVin && selectedVin.vinRecord.vin == vin) {
        return;
    }
    setIsLoading(true);
    try {
        await vinSessionService.openVinSession({
            vin: vin,
            ignoreCache: true,
        });
    } catch (error) {
        handleVinSessionError('Open VIN Session Error', error);
    }
    setIsLoading(false);
}

export async function closeVinSession(
    vin: any,
    accountId: any,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    dispatch: ReturnType<typeof useVinSessionDispatch>
) {
    setIsLoading(true);
    try {
        await vinSessionService.closeVinSession({
            vin: vin,
            ignoreCache: true,
        });
        dispatch(vinSessionActions.setVinBannerOpen({ vinBannerOpen: false }));
    } catch (error) {
        handleVinSessionError('Close VIN Session Error', error);
    }
    setIsLoading(false);
}

export async function getVin(vin: string, setIsLoading: React.Dispatch<React.SetStateAction<boolean>>) {
    try {
        const response = await vinSessionService.getVinData({
            fetchVinParams: {
                vin: vin,
            },
            ignoreCache: true,
        });
        if (response.success) {
            return response.data;
        }
    } catch (error) {
        window.alert(`Vin Fetching Error: ${error.message}`);
    }
}

async function fetchAccountDocsTableData(
    type: AccountDocsType,
    setPublications: any,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    errorMessage: string,
    selectedVin?: VIN,
    setPageDetail?: React.Dispatch<React.SetStateAction<PageDetail>>
) {
    setIsLoading(true);
    try {
        const response = await docsService.getAccountDocs({
            queryParameters: {
                type,
                carlineId: selectedVin?.carlineId,
                modelYear: selectedVin?.vinRecord.modelYear,
                page: 0,
                size: 10,
            },
        });
        if (response.success) {
            setPublications(response.data.content);
            if (setPageDetail) {
                setPageDetail(response.data.page);
            }
        }
    } catch (error) {
        handleDocsError(errorMessage, error);
    }
    setIsLoading(false);
}

export async function fetchFavoritesTableData(
    setPublications: any,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    selectedVin?: VIN,
    setPageDetail?: React.Dispatch<React.SetStateAction<PageDetail>>
) {
    return fetchAccountDocsTableData(
        'FAVORITE',
        setPublications,
        setIsLoading,
        `Favorites Docs Fetching Error: No Record Found; an unknown error occurred`,
        selectedVin,
        setPageDetail
    );
}

export async function fetchWhatsNewData(
    setPublications: any,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setPageDetail?: React.Dispatch<React.SetStateAction<PageDetail>>,
    params?: string
) {
    setIsLoading(true);
    try {
        const response = await docsService.getWhatsNew({
            ignoreCache: true,
        });
        if (response.success) {
            setPublications(response.data.content);
            if (setPageDetail) {
                setPageDetail(response.data.page);
            }
        }
    } catch (error) {
        handleDocsError(`Whats New Docs Fetching Error: No Record Found; an unknown error occurred`, error);
    }
    setIsLoading(false);
}

export async function fetchRecentlyViewedData(
    setPublications: any,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    selectedVin?: VIN,
    setPageDetail?: React.Dispatch<React.SetStateAction<PageDetail>>
) {
    return fetchAccountDocsTableData(
        'RECENT',
        setPublications,
        setIsLoading,
        `Recently-Viewed Docs Fetching Error: No Record Found; an unknown error occurred`,
        selectedVin,
        setPageDetail
    );
}

export const handleCopy = async (
    copyText: string,
    dispatch: VinSessionActionDispatcher | PublicationsActionDispatcher,
    action: any,
    toastMessage: string,
    iconColor?: string,
    iconName?: string
) => {
    try {
        await navigator.clipboard.writeText(copyText);
        dispatch(
            action({
                toast: {
                    toastMessage: toastMessage,
                    iconColor: iconColor,
                    iconName: iconName,
                },
            })
        );
    } catch (error) {
        handleVinSessionError('Failed to copy:', error);
    }
};

export function toBoolean(value: boolean | string | null | undefined): boolean {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') {
            return true;
        }
        if (normalized === 'false') {
            return false;
        }
    }

    return false;
}
